import { ReactNode, useEffect, useState } from 'react';
import { signalRToastWithAnalytics } from '../shared/analytics';
import useGetDeals from '../shared/hooks/useGetDeals';
import useSignalRConnector from '../shared/hooks/useSignalRConnector';
import RenderHtml from '../shared/components/RenderHtml';

interface AddDealUsersCompletedResponse {
  agreementId: string;
  totalProducts: number;
  domains: AddDealUsersCompletedDomainTypeResponse;
  contacts: AddDealUsersCompletedEmailTypeResponse;
}

interface AddDealUsersCompletedDomainTypeResponse {
  completed: {
    name: string;
    blacklist: string[];
  }[];
  failed: {
    name: string;
    blacklist: string[];
  }[];
}

interface AddDealUsersCompletedEmailTypeResponse {
  total: number;
  completed: number;
  failed: {
    email: string;
    phoneNumber: string;
  }[];
}

export default function useAddDealUsersCompletedSignalR() {
  const { data, isFetching, isLoading, isError } = useGetDeals({
    allDealsEnabled: true,
  });
  const [dealsWrapper] = useState({ data });

  useEffect(() => {
    if (!isFetching && !isLoading && !isError) dealsWrapper.data = data;
  }, [isFetching, isLoading, isError]);

  const generateFailedMessage = (
    failedItems: { email?: string; name?: string }[],
    label: string,
    property: 'email' | 'name',
  ): ReactNode => (
    <>
      The following {label} failed to be invited:{' '}
      <span data-exclude-lg>
        {failedItems.map((item) => (
          <span>
            <RenderHtml html={item[property]} tag="span" />,{' '}
          </span>
        ))}
      </span>
    </>
  );

  useSignalRConnector(
    'adddealuserscompleted',
    (response: AddDealUsersCompletedResponse) => {
      if (response.contacts?.completed) {
        const isMultiInvites = response.contacts?.completed > 1;
        signalRToastWithAnalytics(
          <span>
            You have assigned {response.totalProducts}
            {response.totalProducts > 1 ? ' products' : ' product'} to{' '}
            {response.contacts.completed}
            {isMultiInvites ? ' people' : ' person'} from agreement:
            <br />
            <span className="text-decoration-underline">
              {response.agreementId}
            </span>
          </span>,
          'success',
        );
      }

      // for email invites - failure
      if (response.contacts?.failed?.length) {
        signalRToastWithAnalytics(
          generateFailedMessage(response.contacts.failed, 'users', 'email'),
          'danger',
        );
      }

      // for domain invites - success
      if (response.domains?.completed?.length) {
        let domainMessage = '';
        if (response.domains.completed.length === 1) {
          domainMessage = `${response.domains.completed[0].name} has been set up for ${response.agreementId}`;
        } else {
          domainMessage = `${response.domains.completed.length} domains have been set up for ${response.agreementId}`;
        }

        let totalExcluded = 0;
        response.domains.completed.forEach((e) => {
          totalExcluded += e.blacklist?.length ?? 0;
        });

        if (totalExcluded > 0)
          domainMessage += ` with ${totalExcluded} email${
            totalExcluded > 1 ? 's' : ''
          } excluded`;

        if (domainMessage)
          signalRToastWithAnalytics(`${domainMessage}.`, 'success');
      }

      // for domain invites - failure
      if (response.domains?.failed?.length) {
        signalRToastWithAnalytics(
          generateFailedMessage(response.domains.failed, 'domains', 'name'),
          'danger',
        );
      }
    },
  );
}
